// internal/SecurityPage.jsx — Security & Governance : identités/recovery, mode auth, JSONFIRST Policy Center (visuel/JSON/simulateur/versions), Founder Twin + Emergency Stop.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import InternalLayout, { S, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const RISK = { low: "#8BA394", medium: "#F59E0B", high: "#FB923C", critical: "#F87171" };
const RESULT = { ALLOWED: "#2EE27B", REQUIRES_APPROVAL: "#E8B84B", DENIED: "#F87171" };
const inp = { width: "100%", boxSizing: "border-box", background: "#0D1310", border: `1px solid ${IT.border}`, borderRadius: 10, padding: "9px 12px", color: IT.text, fontSize: 12.5, outline: "none", fontFamily: "inherit" };

function Simulator() {
  const [intent, setIntent] = useState("");
  const [sim, setSim] = useState(null);
  const run = async () => {
    const r = await fetch(`${API}/security/policies/simulate`, { method: "POST", headers: H(), body: JSON.stringify({ intent }) });
    const d = await r.json();
    if (d.ok) setSim(d.simulation); else toast.error(d.error);
  };
  return (
    <div style={{ ...S.card, padding: 14, marginBottom: 12, border: "1px solid rgba(232,184,75,0.3)" }} data-testid="policy-simulator">
      <div style={{ fontSize: 12.5, fontWeight: 900, color: IT.gold, marginBottom: 8 }}>🧪 Simulateur JSONFIRST (aucune exécution réelle)</div>
      <div style={{ display: "flex", gap: 7 }}>
        <input data-testid="sim-intent" style={{ ...inp, flex: 1 }} value={intent} onChange={(e) => setIntent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder='Ex : "Déployer AYA Transport en production"' />
        <button data-testid="sim-run" onClick={run} disabled={!intent.trim()} style={S.btn}>Simuler</button>
      </div>
      {sim && (
        <div style={{ marginTop: 10, fontSize: 12, display: "flex", flexDirection: "column", gap: 4 }} data-testid="sim-result">
          <span>Intent → <strong>{sim.detected_trigger}</strong> · Risk → <strong style={{ color: RISK[sim.risk] }}>{sim.risk}</strong></span>
          <span>Policy → <strong>{sim.policy || "aucune"}</strong></span>
          <span>Result → <strong style={{ color: RESULT[sim.result] }}>{sim.result}</strong>{sim.snapshot_required ? " · snapshot requis" : ""}</span>
          <span style={{ color: IT.muted }}>Reason → {sim.reason}</span>
        </div>
      )}
    </div>
  );
}

function Policies() {
  const [rows, setRows] = useState([]);
  const [jsonView, setJsonView] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => { fetch(`${API}/security/policies`, { headers: H() }).then((r) => r.json()).then((d) => d.ok && setRows(d.policies)); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const r = await fetch(`${API}/security/policies`, { method: "POST", headers: H(), body: JSON.stringify(form) });
    const d = await r.json();
    if (d.ok) { toast.success(`Policy créée (${d.policy.status})`); setForm(null); load(); } else toast.error(d.error);
  };
  const publish = async (p) => {
    const r = await fetch(`${API}/security/policies/${p.policy_id}/publish`, { method: "POST", headers: H(), body: JSON.stringify({ founder_confirmed: window.confirm(`Publication Founder : activer « ${p.name} » ?`) }) });
    const d = await r.json();
    if (d.ok) { toast.success("Policy publiée"); load(); } else toast.error(d.message || d.error);
  };
  return (
    <>
      <Simulator />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button data-testid="policy-new" onClick={() => setForm({ name: "", description: "", trigger: "deployment.production", action: "require_approval", risk: "high" })} style={S.btn}>➕ Nouvelle règle</button>
      </div>
      {form && (
        <div style={{ ...S.card, padding: 14, marginBottom: 12 }} data-testid="policy-editor">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input data-testid="policy-name" style={inp} placeholder="NOM_DE_LA_REGLE" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="policy-trigger" style={inp} placeholder="Déclencheur (ex deployment.production)" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} />
            <select data-testid="policy-action" style={inp} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}>
              <option value="require_approval">require_approval</option><option value="deny">deny</option><option value="allow">allow</option>
            </select>
            <select data-testid="policy-risk" style={inp} value={form.risk} onChange={(e) => setForm({ ...form, risk: e.target.value })}>
              {["low", "medium", "high", "critical"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <input data-testid="policy-desc" style={{ ...inp, marginTop: 8 }} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={() => setForm(null)} style={{ ...S.ghost, color: IT.muted }}>Annuler</button>
            <button data-testid="policy-save" onClick={create} style={S.btn}>💾 Créer {form.risk === "critical" ? "(draft — double validation)" : ""}</button>
          </div>
        </div>
      )}
      {rows.map((p) => (
        <div key={p.policy_id} style={{ ...S.card, padding: 13, marginBottom: 8 }} data-testid={`policy-${p.name}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 12.5, fontFamily: "monospace" }}>{p.name}</strong>
            <span style={{ fontSize: 9, fontWeight: 900, color: RISK[p.risk], border: `1px solid ${RISK[p.risk]}55`, borderRadius: 999, padding: "2px 8px" }}>{p.risk}</span>
            <span style={{ fontSize: 9, fontWeight: 900, color: p.status === "active" ? IT.green : IT.gold, border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "2px 8px" }}>{p.status} · v{p.version}</span>
            {p.founder_only && <span style={{ fontSize: 9, fontWeight: 900, color: "#F87171", border: "1px solid rgba(248,113,113,0.4)", borderRadius: 999, padding: "2px 8px" }}>FOUNDER-ONLY</span>}
            <span style={{ flex: 1 }} />
            {p.status !== "active" && <button data-testid={`policy-publish-${p.name}`} onClick={() => publish(p)} style={{ ...S.ghost, padding: "4px 10px", fontSize: 10.5 }}>Publier</button>}
            <button data-testid={`policy-json-${p.name}`} onClick={() => setJsonView(jsonView === p.policy_id ? null : p.policy_id)} style={{ ...S.ghost, padding: "4px 10px", fontSize: 10.5 }}>{jsonView === p.policy_id ? "Visuel" : "JSON"}</button>
          </div>
          <div style={{ fontSize: 11.5, color: IT.muted, marginTop: 5 }}>{p.trigger} → <strong style={{ color: IT.text }}>{p.action}</strong> · {p.description}</div>
          {jsonView === p.policy_id && (
            <pre style={{ marginTop: 8, padding: 10, borderRadius: 10, background: "#0D1310", border: `1px solid ${IT.border}`, fontSize: 10.5, overflowX: "auto", color: "#A7F3C9" }} data-testid="policy-json-view">{JSON.stringify({ ...p, versions: undefined }, null, 2)}
--- HISTORIQUE ---
{(p.versions || []).map((v) => `v${v.v} · ${v.author} · ${new Date(v.date).toLocaleDateString("fr-FR")} · ${v.change}`).join("\n")}</pre>
          )}
        </div>
      ))}
    </>
  );
}

function Twin() {
  const [t, setT] = useState(null);
  const [intent, setIntent] = useState("");
  const [report, setReport] = useState(null);
  const load = useCallback(() => { fetch(`${API}/security/twin`, { headers: H() }).then((r) => r.json()).then((d) => d.ok && setT(d.twin)); }, []);
  useEffect(() => { load(); }, [load]);
  const call = async (path, body) => {
    const r = await fetch(`${API}/security/twin/${path}`, { method: path === "" ? "GET" : "POST", headers: H(), body: body ? JSON.stringify(body) : undefined });
    return r.json();
  };
  if (!t) return <div style={{ color: IT.muted, fontSize: 13 }}>Chargement…</div>;
  const stColor = t.emergency_stopped ? "#F87171" : t.status === "continuity" ? IT.gold : IT.muted;
  return (
    <>
      <div style={{ ...S.card, padding: 16, marginBottom: 12 }} data-testid="twin-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 22 }}>🧬</span>
          <span style={{ flex: 1 }}>
            <strong style={{ fontSize: 15 }}>AYA Founder Twin</strong>
            <span style={{ display: "block", fontSize: 11, color: IT.muted }}>Mandataire numérique de continuité — jamais propriétaire de l'identité Founder</span>
          </span>
          <span data-testid="twin-status" style={{ fontSize: 10, fontWeight: 900, color: stColor, border: `1px solid ${stColor}66`, borderRadius: 999, padding: "3px 11px" }}>
            {t.emergency_stopped ? "EMERGENCY STOP" : t.status.toUpperCase()}
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: IT.muted, margin: "12px 0", lineHeight: 1.7 }}>
          <strong style={{ color: IT.green }}>Autorisé :</strong> {t.mandate.allowed.join(" · ")}<br />
          <strong style={{ color: "#F87171" }}>Interdit :</strong> {t.mandate.blocked.join(" · ")}<br />
          <strong style={{ color: IT.text }}>Mandat v{t.mandate.version}</strong> · autonomie {t.autonomy}/5 · risque max {t.mandate.max_risk} · durée {t.mandate.duration_h}h · workspaces {t.mandate.workspaces.join(", ")}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {t.status !== "continuity" && !t.emergency_stopped && (
            <button data-testid="twin-activate" onClick={async () => { const d = await call("activate", { reason: "Activation par le Founder" }); d.ok ? (toast.success("Continuity Mode actif"), load()) : toast.error(d.message || d.error); }} style={S.btn}>▶ Activer Continuity Mode</button>
          )}
          {t.status === "continuity" && (
            <button data-testid="twin-deactivate" onClick={async () => { const d = await call("deactivate"); if (d.ok) { setReport(d.report); toast.success("Continuité terminée — rapport généré"); load(); } }} style={{ ...S.btn, background: "linear-gradient(180deg,#E8B84B,#C99A2E)" }}>⏹ Retour Founder (rapport)</button>
          )}
          <button data-testid="twin-emergency" onClick={async () => { const d = await call("emergency-stop", { rearm: t.emergency_stopped }); if (d.ok) { toast.success(d.emergency_stopped ? "🛑 EMERGENCY STOP" : "Réarmé"); load(); } }}
            style={{ ...S.ghost, borderColor: "rgba(248,113,113,0.5)", color: "#F87171", background: "rgba(248,113,113,0.07)" }}>🛑 {t.emergency_stopped ? "Réarmer" : "EMERGENCY STOP"}</button>
        </div>
      </div>

      {t.status === "continuity" && (
        <div style={{ ...S.card, padding: 14, marginBottom: 12, border: "1px solid rgba(232,184,75,0.35)" }} data-testid="twin-action-panel">
          <div style={{ fontSize: 12, fontWeight: 900, color: IT.gold, marginBottom: 8 }}>Continuité active — chaque action du Twin passe par JSONFIRST</div>
          <div style={{ display: "flex", gap: 7 }}>
            <input data-testid="twin-action-intent" style={{ ...inp, flex: 1 }} value={intent} onChange={(e) => setIntent(e.target.value)} placeholder='Intention du Twin (ex "Superviser les incidents tech" / "Modifier mon propre mandat")' />
            <button data-testid="twin-action-run" onClick={async () => { const d = await call("action", { intent }); if (d.ok) { toast[d.verdict.result === "ALLOWED" ? "success" : "error"](`${d.verdict.result}${d.verdict.policy ? " · " + d.verdict.policy : ""}`); setIntent(""); load(); } }} style={S.btn}>Soumettre</button>
          </div>
        </div>
      )}

      {(t.actions || []).length > 0 && (
        <div style={{ ...S.card, padding: 14, marginBottom: 12 }} data-testid="twin-actions-log">
          <div style={{ fontSize: 12.5, fontWeight: 900, marginBottom: 8 }}>Journal du Twin</div>
          {t.actions.map((a, i) => (
            <div key={i} style={{ fontSize: 11.5, padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", gap: 8 }}>
              <span style={{ color: RESULT[a.result], fontWeight: 900, minWidth: 150 }}>{a.result}</span>
              <span style={{ flex: 1 }}>{a.intent}</span>
              <span style={{ color: IT.muted }}>{a.policy || ""}</span>
            </div>
          ))}
        </div>
      )}

      {report && (
        <div style={{ ...S.card, padding: 16, border: "1px solid rgba(232,184,75,0.4)" }} data-testid="continuity-report">
          <div style={{ fontSize: 14, fontWeight: 900, color: IT.gold }}>📜 FOUNDER CONTINUITY REPORT</div>
          <div style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.8 }}>
            Bonjour Cédric. AYA a fonctionné en mode continuité pendant <strong>{report.duration_h}h{String(report.duration_min).padStart(2, "0")}</strong>.<br />
            ✅ {report.executed} action(s) exécutée(s) · ⛔ {report.denied} refusée(s) · 🔐 {report.approvals} en attente d'approbation
          </div>
          {report.needs_attention.length > 0 && (
            <div style={{ fontSize: 11.5, color: "#F87171", marginTop: 8 }}>
              <strong>Éléments nécessitant votre attention :</strong>
              {report.needs_attention.map((a, i) => <div key={i}>• {a.intent} — {a.reason || a.result}</div>)}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Identities({ d, reload }) {
  const [tempCode, setTempCode] = useState(null);
  const op = async (phone, o) => {
    const r = await fetch(`${API}/security/identities/${encodeURIComponent(phone)}/${o}`, { method: "POST", headers: H() });
    const x = await r.json();
    if (x.ok) { toast.success("Fait"); reload(); } else toast.error(x.error);
  };
  const grantTemp = async (phone) => {
    const dur = Number(window.prompt("Durée de l'autorisation temporaire (minutes) :", "60"));
    if (!dur) return;
    const r = await fetch(`${API}/security/identities/${encodeURIComponent(phone)}/grant-temporary`, { method: "POST", headers: H(), body: JSON.stringify({ duration_min: dur }) });
    const x = await r.json();
    if (x.ok) { setTempCode({ phone, code: x.code, expires: x.expires_at }); toast.success("Autorisation temporaire créée"); reload(); }
    else toast.error(x.error === "identity_not_enrolled" ? "Ce collaborateur n'est pas encore enrôlé." : x.error);
  };
  return (
    <>
      <div style={{ ...S.card, padding: 14, marginBottom: 12 }} data-testid="auth-mode-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, fontWeight: 900, flex: 1 }}>AUTHENTICATION MODE <span style={{ display: "block", fontSize: 10.5, color: IT.muted, fontWeight: 600 }}>L'expérimentation biométrique ne devient jamais automatiquement la dépendance de production (§52).</span></span>
          {["experimental", "production"].map((m) => (
            <button key={m} data-testid={`auth-mode-${m}`} onClick={async () => { const r = await fetch(`${API}/security/auth-mode`, { method: "POST", headers: H(), body: JSON.stringify({ mode: m }) }); const x = await r.json(); x.ok ? (toast.success(`Mode ${m.toUpperCase()}`), reload()) : toast.error(x.error); }}
              style={{ padding: "8px 16px", borderRadius: 999, fontSize: 11, fontWeight: 900, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${d.auth_mode === m ? (m === "experimental" ? IT.gold : IT.green) : "rgba(139,163,148,0.3)"}`, background: d.auth_mode === m ? (m === "experimental" ? "rgba(232,184,75,0.12)" : "rgba(46,226,123,0.12)") : "transparent", color: d.auth_mode === m ? (m === "experimental" ? IT.gold : IT.green) : IT.muted }}>
              {m.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div style={{ ...S.card, padding: 14 }} data-testid="identities-list">
        <div style={{ fontSize: 12.5, fontWeight: 900, marginBottom: 8 }}>Identités enrôlées ({d.identities.length})</div>
        {d.identities.length === 0 && <div style={{ fontSize: 12, color: IT.muted }}>Aucune identité enrôlée — les utilisateurs passeront par Secure Welcome à leur prochaine connexion.</div>}
        {d.identities.map((i) => (
          <div key={i.phone} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" }} data-testid={`identity-${i.phone}`}>
            <span style={{ flex: 1, minWidth: 160, fontSize: 12.5 }}>
              <strong>{i.display_name || i.phone}</strong> <span style={{ color: i.identity_type === "founder" ? IT.gold : IT.muted, fontSize: 10.5 }}>· {i.identity_type} · {i.security_profile}</span>
              <span style={{ display: "block", fontSize: 10.5, color: IT.muted }}>passkey {i.methods?.passkey ? "✓" : "—"} · présence exp. {i.methods?.experimental_presence ? "✓" : "—"} · {Array.isArray(i.trusted_devices) ? i.trusted_devices.length : (i.trusted_devices || 0)} appareil(s)</span>
            </span>
            <span style={{ fontSize: 9.5, fontWeight: 900, color: i.status === "active" ? IT.green : "#F87171", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "2px 9px" }}>{i.status}</span>
            {i.identity_type !== "founder" && (
              <>
                <button data-testid={`identity-temp-${i.phone}`} onClick={() => grantTemp(i.phone)} style={{ ...S.ghost, padding: "4px 10px", fontSize: 10.5, borderColor: "rgba(232,184,75,0.4)", color: IT.gold }}>⏱ Accès temporaire</button>
                <button data-testid={`identity-suspend-${i.phone}`} onClick={() => op(i.phone, i.status === "active" ? "suspend" : "reactivate")} style={{ ...S.ghost, padding: "4px 10px", fontSize: 10.5 }}>{i.status === "active" ? "Suspendre" : "Réactiver"}</button>
                <button onClick={() => window.confirm("Réinitialiser l'enrôlement ?") && op(i.phone, "reset-enrollment")} style={{ ...S.ghost, padding: "4px 10px", fontSize: 10.5, borderColor: "rgba(248,113,113,0.35)", color: "#F87171" }}>Reset</button>
              </>
            )}
          </div>
        ))}
        {tempCode && (
          <div style={{ marginTop: 10, padding: 12, borderRadius: 11, background: "rgba(232,184,75,0.07)", border: "1px solid rgba(232,184,75,0.4)" }} data-testid="temp-code-box">
            <div style={{ fontSize: 11, fontWeight: 900, color: IT.gold }}>CODE TEMPORAIRE pour {tempCode.phone} — affiché UNE SEULE FOIS, usage unique, expire le {new Date(tempCode.expires).toLocaleString("fr-FR")}</div>
            <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 900, marginTop: 6, color: "#EAFDF3" }}>{tempCode.code}</div>
          </div>
        )}
        <div style={{ fontSize: 11, color: IT.muted, marginTop: 10 }}>Recovery : {d.recovery.map((r) => `${r.phone.slice(-4)} (${r.codes_remaining} codes)`).join(" · ") || "—"}</div>
      </div>
    </>
  );
}

export default function SecurityPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("identities");
  const [d, setD] = useState(null);
  const load = useCallback(() => { fetch(`${API}/security/overview`, { headers: H() }).then((r) => r.json()).then((x) => x.ok && setD(x)); }, []);
  useEffect(() => { load(); }, [load]);
  const TABS = [["identities", "🪪 Identités & Auth"], ["policies", "⚖️ JSONFIRST Policies"], ["twin", "🧬 Founder Twin"], ["approvals", "🔐 Approbations"]];
  return (
    <InternalLayout title="Security & Governance" subtitle="Identités · Authentication Mode · JSONFIRST Policy Center · Founder Twin · Continuité">
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14 }}>
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`sec-tab-${k}`} onClick={() => (k === "approvals" ? navigate("/internal/approvals") : setTab(k))}
            style={{ padding: "8px 15px", borderRadius: 999, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${tab === k ? IT.green : "rgba(139,163,148,0.3)"}`, background: tab === k ? "rgba(46,226,123,0.12)" : "transparent", color: tab === k ? IT.green : IT.muted }}>{l}</button>
        ))}
      </div>
      {!d && <div style={{ color: IT.muted, fontSize: 13 }}>Chargement…</div>}
      {d && tab === "identities" && <Identities d={d} reload={load} />}
      {d && tab === "policies" && <Policies />}
      {d && tab === "twin" && <Twin />}
    </InternalLayout>
  );
}
